/**
 * A FAULT REPORTS ITSELF — #265.
 *
 * Vinnie, 25 August 2026: "I think error reporting is extremely critical, and
 * needs to be done. We have hit a few errors today alone, and we need to know
 * how to fix it, and in order to do that, we need to know what is broken."
 *
 * WHAT WAS ALREADY TRUE, AND WHY THIS FILE IS SMALL. Every fault has been
 * captured for weeks — `watchForTheTrail` hooks window errors and unhandled
 * rejections, the error boundary records a render that threw, and a few writes
 * report their own failure. All of it reached `remember('broke', …)` and stopped
 * there, in one browser, waiting for somebody to find the feedback form. The
 * missing piece was never the capturing.
 *
 * IT COST A DAY ON 30 AUGUST 2026. The Releases screen hung — on live, since
 * the screen was written. The product did exactly what it promised: it put a
 * reference on the broken screen, K5W-WVQ and then ZEX-GYF, and asked Vinnie to
 * quote it. Neither resolved to anything. The only reason that fault is known
 * is that the person it happened to builds this.
 *
 * ── WHAT IT SENDS, AND WHAT IT REFUSES TO ────────────────────────────────
 *
 * The message, the stack, the build (version AND commit — "it broke" against
 * unknown code is not a diagnosis), the screen by name, the reference already
 * shown, and the trail. Who and which workspace when there is a signed-in
 * person to name, and nothing when there is not.
 *
 * NOT THE ADDRESS. Since #218 the address carries the company slug, and the row
 * already names the workspace properly. Not anything typed: `src/trail.ts`
 * refuses keystrokes, card titles and board names by construction, and says so
 * at the top.
 *
 * ⛔ AND NOT TO A THIRD PARTY. Sentry and its like would take all of the above
 * and often a recording of the session. That makes them a sub-processor of an
 * insurance company's data — a contract and a due-diligence questionnaire, not
 * a script tag. This goes to our own function and our own table.
 *
 * ── WHY IT COUNTS INSTEAD OF SENDING ─────────────────────────────────────
 *
 * The white page on 25 August was a render loop: the same error thrown
 * continuously. Sent naively that is thousands of requests a minute from one
 * browser, thousands of rows, a bill, and the one report that mattered buried
 * under identical copies of the one that did not.
 *
 * So a fault is sent ONCE when it is first seen, and after that it is only
 * counted — flushed every half minute as a number. The table adds that number
 * to what it already has, so `seen` is the real count rather than the number of
 * times the network happened to be quick.
 */
import { SUPABASE_URL, SUPABASE_ANON_KEY } from './supabase';
import { trail, whenSomethingBreaks, type TrailEntry } from './trail';

/** Distinct faults reported per page load. Past this the page is on fire. */
const MOST_KINDS = 25;
/** How often repeats are flushed as a count. */
const EVERY = 30_000;

const seen = new Map<string, { times: number; sent: boolean }>();
let stopped = false;

/**
 * The reference the person was shown, when they were shown one.
 *
 * Set by the error boundary as it renders the broken screen. It is the only
 * thing tying "somebody rang up quoting ZEX-GYF" to a row that arrived without
 * them, which is the whole point of the card.
 */
let shown: string | undefined;
export function theReferenceOnScreen(ref: string): void {
  shown = ref;
}

/**
 * READ ONCE AND FORGOTTEN, which is not tidiness.
 *
 * A reference belongs to exactly ONE fault — the one the person was looking at
 * when they were shown it. Left set, it lands on every fault after it too, and
 * then a support call quoting it matches three rows that have nothing to do with
 * each other. Walking this on the test copy is how that was found: the summoned
 * break got 782-34M correctly, and so did an unrelated failure two seconds later.
 */
const theReference = (): string | undefined => {
  const ref = shown;
  shown = undefined;
  return ref;
};

/**
 * WHAT MAKES TWO FAULTS THE SAME ONE.
 *
 * The message with its numbers flattened, because `Cannot read x of undefined
 * at 14:32:07` and the same at 14:32:08 are one fault. Not the stack: minified
 * frame names change between builds, and the build is part of the row's
 * identity already.
 */
const fingerprintOf = (what: string): string =>
  what
    .replace(/ ×\d+$/, '')
    .replace(/\b[0-9a-f]{8,}\b/gi, '#')
    .replace(/\d+/g, '#')
    .slice(0, 200);

/** The screen by its own name, read where the app already keeps it. */
const whichScreen = (): string | undefined => {
  try {
    return localStorage.getItem('flightdeck-view') ?? undefined;
  } catch {
    return undefined;
  }
};

/**
 * The signed-in token, if there is one. Read the way src/workspace.ts reads it
 * rather than through the client, because this has to work on a page where the
 * app itself has failed to start.
 */
const token = (): string | undefined => {
  try {
    const key = Object.keys(localStorage).find((k) => k.startsWith('sb-') && k.endsWith('-auth-token'));
    if (!key) return undefined;
    return JSON.parse(localStorage.getItem(key) ?? '{}')?.access_token || undefined;
  } catch {
    return undefined;
  }
};

async function post(body: Record<string, unknown>, keepalive = false): Promise<void> {
  const bearer = token();
  await fetch(`${SUPABASE_URL}/functions/v1/broke`, {
    method: 'POST',
    keepalive,
    headers: {
      'Content-Type': 'application/json',
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${bearer || SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify(body),
  });
}

/**
 * A FAILED REPORT IS NEVER RETRIED AND NEVER SHOWN.
 *
 * Retrying would turn an outage into a flood the moment the network came back,
 * and telling somebody their error report failed to send is a second error
 * message about the first one, which helps nobody and frightens some.
 */
const quietly = (p: Promise<void>): void => {
  void p.catch(() => {
    /* deliberate — see above */
  });
};

function report(entry: TrailEntry, times: number, reference?: string, stack?: string, keepalive = false): void {
  quietly(
    post(
      {
        fingerprint: fingerprintOf(entry.what),
        message: entry.what,
        kind: entry.what.startsWith('render:') ? 'render' : entry.what.startsWith('unhandled:') ? 'rejection' : 'error',
        reference,
        /* Capped: a stack is worth having and is not worth a megabyte of it. */
        stack: stack?.slice(0, 8000),
        /*
         * THE DEFINES, NOT `globalThis`. Vite replaces these identifiers with
         * literals at build time; it does not put them on any object, so reading
         * them by name off `globalThis` returns undefined in a browser — which
         * it did, and three of the most useful fields on the row arrived empty.
         * "It broke" against unknown code is not a diagnosis.
         */
        version: __APP_VERSION__,
        commit: __BUILD_COMMIT__,
        env: __ENV__,
        screen: whichScreen(),
        trail: trail(),
        agent: navigator.userAgent,
        times,
      },
      keepalive
    )
  );
}

/** Everything counted since the last flush, as one call each. */
function flush(keepalive = false): void {
  for (const [key, state] of seen) {
    if (state.times === 0) continue;
    const times = state.times;
    state.times = 0;
    report({ at: Date.now(), kind: 'broke', what: key }, times, undefined, undefined, keepalive);
  }
}

/**
 * Start listening. Called once, from main.tsx, beside `watchForTheTrail`.
 *
 * NOT IN THE OPERATOR PORTAL. It has its own main.tsx which deliberately starts
 * neither this nor the trail — see the note there. Staff faults are ours to
 * find by looking, and a portal reporting into the table it reads is a loop
 * waiting to be written.
 */
export function reportWhatBreaks(): void {
  whenSomethingBreaks((entry, stack) => {
    if (stopped) return;
    /* Taken here rather than inside `report`, so a fault that is only counted
       still spends it — the row already keeps the first reference it was given,
       and a reference left lying about lands on the next unrelated fault. */
    const reference = theReference();
    const key = fingerprintOf(entry.what);
    const state = seen.get(key);
    if (state) {
      state.times += 1;
      return;
    }
    /*
     * PAST TWENTY-FIVE DISTINCT FAULTS the page is not having a problem, it IS
     * the problem, and every further kind is dropped rather than sent. Stopping
     * is recorded once so the silence afterwards is a fact rather than a gap.
     */
    if (seen.size >= MOST_KINDS) {
      stopped = true;
      report({ at: Date.now(), kind: 'broke', what: `stopped reporting after ${MOST_KINDS} kinds of fault` }, 1);
      return;
    }
    seen.set(key, { times: 0, sent: true });
    report(entry, 1, reference, stack);
  });

  setInterval(() => flush(), EVERY);
  /*
   * AND ON THE WAY OUT, because the interesting case is somebody closing a tab
   * that has been throwing — the count would otherwise die with the page.
   * `pagehide` rather than `unload`: it is the one that fires on a phone.
   */
  window.addEventListener('pagehide', () => flush(true));
}
